import {Component, OnDestroy, OnInit} from '@angular/core';
import {BookingV2ManagerService} from '../../services/booking-v2-manager.service';
import {BookingV2DataService} from '../../services/booking-v2-data.service';
import {Subscription} from 'rxjs';
import * as dayjs from 'dayjs';
import {BookingProviderDetails} from '../../types/booking-provider-details';
import {BookingV2StateService} from '../../services/booking-v2-state.service';
import {BookingStepsEnum} from '../../enums/booking-enums';
import {ModalCommand} from '../../../../types/ModalCommand';
import {ModalTypeEnum} from '../../../../enums/ModalEnums';
import {CareproviderMapItemTranslated} from '../../../../types/CareproviderMapItemTranslated';
import {ModalsControlService} from '../../../../services/modals-control.service';
import {BookingProviderDetailsLink} from '../../types/booking-provider-details-link';
import {BookingServiceDetail} from '../../../../types/BookingServiceDetail';
import {BookingState} from '../../types/booking-state';

@Component({
  selector: 'app-booking-summary',
  templateUrl: './booking-summary.component.html',
  styleUrls: ['./booking-summary.component.scss'],
})
export class BookingSummaryComponent implements OnInit, OnDestroy {
  public providerDetails: BookingProviderDetails;
  public step: number;
  public bookingStepsEnum = BookingStepsEnum;
  public isExpanded = false;
  public selectedBookingService: BookingServiceDetail;
  public selectedGroup: BookingProviderDetailsLink;
  public selectedDateTimeForDisplay: string;
  public practitionerName: string;
  private subscriptions: Subscription[] = [];

  constructor(
    private bookingManager: BookingV2ManagerService,
    private bookingData: BookingV2DataService,
    private bookingState: BookingV2StateService,
    private modalControl: ModalsControlService,
  ) {}

  public ngOnInit(): void {
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  public navigateToCareproviderUnit(): void {
    this.bookingManager.navigateToCareproviderUnit(false);
  }

  public openMap(): void {
    const modalCommand: ModalCommand = {
      type: ModalTypeEnum.GOOGLE_MAP,
      data: {height: 80, markers: this.prepareMarkerForProvider()},
    };
    this.modalControl.openModal(modalCommand);
  }

  public toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  public openTreatmentDetailsModal(): void {
    this.bookingManager.openTreatmentDetailsModal(this.selectedBookingService, this.selectedGroup.careServiceId);
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.bookingData.providerDetails$.subscribe((result) => this.handleProviderDetails(result)),
      this.bookingState.state$.subscribe((result) => {
        this.step = result.step;
        this.processBookingState(result);
      }),
    );
  }

  private prepareMarkerForProvider(): CareproviderMapItemTranslated[] {
    return [
      {
        id: this.providerDetails.id,
        name: this.providerDetails.name,
        urlPath: this.providerDetails.navigation?.urlPath,
        location: {
          lat: this.providerDetails.location.latitude,
          lng: this.providerDetails.location.longitude,
        },
        rating: 0,
        address: this.providerDetails.location,
        thumbnail: this.providerDetails.image.thumbnail,
      },
    ];
  }

  private handleProviderDetails(result: BookingProviderDetails) {
    this.providerDetails = result;
  }

  private processBookingState(state: BookingState): void {
    this.setSelectedDateForDisplay(state.selectedDateTime);
    this.setComponentData(state);
  }

  private setComponentData(state: BookingState): void {
    if (state.selectedBookingServiceId != null) {
      this.selectedBookingService = this.bookingData.getServiceById(state.selectedBookingServiceId);
    }

    if (state.selectedStaffMemberId != null) {
      const selectedStaffMember = this.bookingData.getStaffMemberById(state.selectedStaffMemberId);
      this.practitionerName = selectedStaffMember.name;
    } else {
      this.practitionerName = '';
    }

    if (state.serviceGroup != null) {
      this.selectedGroup = this.bookingData.getGroupById(state.serviceGroup);
    }
  }

  private setSelectedDateForDisplay(dateTime: string): void {
    let newSelectedDateTime = '';
    if (dateTime) {
      const date = dayjs(`${dateTime}`, this.bookingState.selectedDateTimeStorageFormat);
      const dateString: string = `${date.format('dddd DD MMMM')} kl ${date.format('HH:mm')}`;
      newSelectedDateTime = dateString.charAt(0).toUpperCase() + dateString.slice(1);
    }
    this.selectedDateTimeForDisplay = newSelectedDateTime;
  }
}
